import React, { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const SofaModel = ({ config }) => {
  const groupRef = useRef();

  // Create a simple sofa geometry procedurally
  useEffect(() => {
    if (groupRef.current) {
      // Clear existing children
      while (groupRef.current.children.length > 0) {
        groupRef.current.remove(groupRef.current.children[0]);
      }

      // Get fabric color
      const fabricColor = getFabricColor(config.fabric);
      const legColor = getLegColor(config.legFinish);

      // Create materials
      const fabricMaterial = new THREE.MeshStandardMaterial({
        color: fabricColor,
        roughness: 0.8,
        metalness: 0.1,
      });

      const legMaterial = new THREE.MeshStandardMaterial({
        color: legColor,
        roughness: 0.6,
        metalness: config.legFinish === 'chrome' ? 0.8 : 0.2,
      });

      // Create sofa geometry based on configuration
      const isLShape = config.configuration.includes('l-shape');
      const size = config.configuration.includes('2-seater') ? 2 : 3;

      // Main seat
      const seatWidth = isLShape ? size + 1 : size;
      const seatGeometry = new THREE.BoxGeometry(seatWidth, 0.5, 1.5);
      const seat = new THREE.Mesh(seatGeometry, fabricMaterial);
      seat.position.y = 0.5;
      groupRef.current.add(seat);

      // Backrest
      const backrestGeometry = new THREE.BoxGeometry(seatWidth, 1.2, 0.3);
      const backrest = new THREE.Mesh(backrestGeometry, fabricMaterial);
      backrest.position.y = 1.1;
      backrest.position.z = -0.6;
      groupRef.current.add(backrest);

      // Armrests
      const armrestGeometry = new THREE.BoxGeometry(0.4, 0.8, 1.5);
      const leftArmrest = new THREE.Mesh(armrestGeometry, fabricMaterial);
      leftArmrest.position.set(-seatWidth / 2 - 0.2, 0.9, 0);
      groupRef.current.add(leftArmrest);

      const rightArmrest = new THREE.Mesh(armrestGeometry, fabricMaterial);
      rightArmrest.position.set(seatWidth / 2 + 0.2, 0.9, 0);
      groupRef.current.add(rightArmrest);

      // L-Shape extension
      if (isLShape) {
        const extensionGeometry = new THREE.BoxGeometry(1.5, 0.5, 1.5);
        const extension = new THREE.Mesh(extensionGeometry, fabricMaterial);
        const direction = config.configuration.includes('left') ? -1 : 1;
        extension.position.set(direction * (seatWidth / 2 + 0.75), 0.5, 1.5);
        groupRef.current.add(extension);

        // Extension backrest
        const extBackGeometry = new THREE.BoxGeometry(0.3, 1.2, 1.5);
        const extBack = new THREE.Mesh(extBackGeometry, fabricMaterial);
        extBack.position.set(direction * (seatWidth / 2 + 1.35), 1.1, 1.5);
        groupRef.current.add(extBack);
      }

      // Cushions
      const cushionCount = isLShape ? size + 1 : size;
      const cushionSpacing = seatWidth / cushionCount;
      for (let i = 0; i < cushionCount; i++) {
        const cushionGeometry = new THREE.BoxGeometry(cushionSpacing * 0.9, 0.3, 1.3);
        const cushion = new THREE.Mesh(cushionGeometry, fabricMaterial);
        cushion.position.set(
          -seatWidth / 2 + cushionSpacing / 2 + i * cushionSpacing,
          0.9,
          0
        );
        groupRef.current.add(cushion);
      }

      // Legs
      const legGeometry = getLegGeometry(config.legStyle);
      const legPositions = [
        [-seatWidth / 2 + 0.3, 0, 0.6],
        [-seatWidth / 2 + 0.3, 0, -0.6],
        [seatWidth / 2 - 0.3, 0, 0.6],
        [seatWidth / 2 - 0.3, 0, -0.6],
      ];

      if (isLShape) {
        const direction = config.configuration.includes('left') ? -1 : 1;
        legPositions.push(
          [direction * (seatWidth / 2 + 0.3), 0, 2.1],
          [direction * (seatWidth / 2 + 1.2), 0, 2.1]
        );
      }

      legPositions.forEach((pos) => {
        const leg = new THREE.Mesh(legGeometry, legMaterial);
        leg.position.set(...pos);
        groupRef.current.add(leg);
      });
    }
  }, [config]);

  // Slow rotation animation
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.002;
    }
  });

  return <group ref={groupRef} />;
};

// Helper functions
const getFabricColor = (fabricId) => {
  const colors = {
    'performance-linen-beige': '#E5DCC3',
    'velvet-terracotta': '#C07A5F',
    'velvet-forest': '#2F4F4F',
    'leather-cognac': '#8B5A3C',
    'boucle-cream': '#F5F5DC',
  };
  return colors[fabricId] || '#E5DCC3';
};

const getLegColor = (finishId) => {
  const colors = {
    walnut: '#5C4033',
    oak: '#C19A6B',
    chrome: '#C0C0C0',
    black: '#2D2D2D',
  };
  return colors[finishId] || '#5C4033';
};

const getLegGeometry = (legStyle) => {
  switch (legStyle) {
    case 'mid-century':
      // Tapered cylinder
      return new THREE.CylinderGeometry(0.04, 0.06, 0.4, 8);
    case 'modern-metal':
      // Thin cylinder
      return new THREE.CylinderGeometry(0.03, 0.03, 0.4, 8);
    case 'classic-wood':
      // Thicker cylinder
      return new THREE.CylinderGeometry(0.06, 0.06, 0.4, 8);
    default:
      return new THREE.CylinderGeometry(0.04, 0.06, 0.4, 8);
  }
};

export default SofaModel;
